/**
 * TRABAJAR SIN CONEXIÓN (FASE 5.1 lectura + FASE 5.2 cola local + FASE 5.3 sincronización)
 *
 * ENCARGADO puede registrar CONFIRMAR/VERIFICAR/NO_OK/NO_APLICA sobre una
 * línea sin conexión — se guarda como mutación PENDIENTE en IndexedDB
 * (`offline/mutaciones.ts`) y desde FASE 5.3 se envía de verdad a
 * `POST /api/sync` (`offline/sync.ts`), manual o automáticamente al
 * recuperar conexión. OPERARIO sigue siendo sólo lectura, sin ningún
 * control de mutación ni de sincronización en pantalla.
 *
 * No es una pantalla nueva para ENCARGADO y otra para OPERARIO: el
 * paquete que cada uno descarga ya viene acotado por el servidor, y las
 * acciones sólo se pintan si `esEncargado` — misma pantalla, mismo
 * componente, contenido condicionado por rol (encargo §27: "reutilizar
 * detalle offline existente, no crear un segundo producto visual").
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Cargando, EstadoLinea, EstadoOt, fechaHora, Modal, Vacio } from '../componentes/base.tsx';
import { estadoTareaOperario } from './mis-trabajos.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesionOfflineActiva } from '../offline/contextoPerfil.tsx';
import {
  descartarMutacionRechazada,
  leerBlobDocumento,
  leerLineaLocal,
  leerMeta,
  leerOtLocal,
  listarMutacionesDeLinea,
  listarMutacionesDeOt,
  listarMutacionesEvidenciaDeOt,
  listarMutacionesNcDeOt,
  listarMutacionesNoSincronizadas,
  listarOtsLocal,
  type DocumentoOfflineLocal,
  type LineaOfflineLocal,
  type MetaOffline,
  type Mutacion,
  type OtOfflineLocal,
  type PayloadMutacion,
  type PayloadNcCrear,
  type TipoMutacion,
  type TipoMutacionLinea,
  type TipoMutacionNc,
} from '../offline/db.ts';
import {
  crearMutacionEvidencia,
  crearMutacionLinea,
  crearMutacionNcCerrar,
  crearMutacionNcCrear,
  crearMutacionNcIniciarTratamiento,
  descartarMutacionPendiente,
  type DatosNuevaMutacionEvidencia,
} from '../offline/mutaciones.ts';
import { estadoLocal, prepararPaqueteOffline, tieneCapacidadOffline } from '../offline/paquete.ts';
import {
  AVISO_DATOS_DESACTUALIZADOS,
  decidirPorCaducidad,
  instanteBloqueo,
  lineaIniciadaEnDispositivo,
  MOTIVO_TRABAJO_NUEVO_BLOQUEADO,
  nivelCaducidad,
} from '../offline/caducidad.ts';
import { ErrorPerfilBloqueado, perfilActivo } from '../offline/perfil.ts';
import { registrarUrlTemporal, revocarUrlTemporal } from '../offline/urlsTemporales.ts';
import { PanelGestionPerfilOffline } from './offline-perfil.tsx';
import {
  accionesLocalesDisponibles,
  agruparCadenasNc,
  esMutacionActiva,
  esMutacionDeLinea,
  estadoLocalNc,
  proyectarEstadoLinea,
} from '../offline/proyeccion.ts';
import { useConectividad } from '../pwa.ts';
import { api, type PrevistoOperario, type PrevistosOt } from '../api.ts';
import { GUARDADO_EN_DISPOSITIVO, presentarEstado } from '../offline/estados-sync.ts';
import { etiquetaOperacion, rotuloOperacion, suscribirCambiosCola } from '../offline/operaciones.ts';
import { sincronizarPendientes, ultimaSincronizacionFueSesionCaducada, ultimaSincronizacionSinIdentidad, useEstadoSync } from '../offline/sync.ts';
import { AvisoPerfilDeOtroUsuario } from '../componentes/aviso-perfil-otro-usuario.tsx';

/**
 * FASE 5.7-A §9/§33 — mensaje único para cuando una lectura local falla
 * (AES-GCM "falla cerrada" ante corrupción: `ErrorDescifrado`, u otro fallo
 * de IndexedDB). Nunca el error técnico crudo (§33: nada de
 * `OperationError`/`DataError`/`ciphertext`/`AES-GCM` como mensaje
 * principal) y nunca una pantalla de carga que se queda girando para
 * siempre — antes de este hardening, un `Promise.all`/`.then()` sin
 * `catch` en el efecto de carga dejaba el estado en `null` para siempre
 * ante cualquier fallo, mostrando `<Cargando/>` eternamente sin ningún
 * mensaje. La recuperación real es la misma en los tres sitios que usan
 * esto: volver a preparar el paquete offline con conexión.
 */
const MENSAJE_FALLO_LECTURA_LOCAL =
  'No se han podido leer los datos guardados en este dispositivo. Puede que estén dañados — vuelve a preparar los datos sin conexión la próxima vez que tengas conexión.';

function horasRestantes(iso: string): number {
  return (new Date(iso).getTime() - Date.now()) / 36e5;
}

function textoValidez(meta: MetaOffline): string {
  //  F14.2 — tres tramos (`offline/caducidad.ts`): pasadas 24 h se sigue trabajando con aviso; pasadas 72 h sólo se
  //  termina lo ya empezado. Nunca se borra nada pendiente.
  const nivel = nivelCaducidad(meta);
  if (nivel === 'CADUCADO') return 'Más de 72 h: sólo puedes terminar lo que ya empezaste. Conéctate para actualizar.';
  if (nivel === 'AVISO') {
    const restantes = Math.max(0, Math.round((instanteBloqueo(meta) - Date.now()) / 36e5));
    return `${AVISO_DATOS_DESACTUALIZADOS}. Puedes seguir trabajando (${String(restantes)} h más para empezar trabajo nuevo).`;
  }
  const horas = horasRestantes(meta.validoOperativamenteHasta);
  //  Redondear los MINUTOS totales primero, nunca horas y minutos por
  //  separado: redondear cada uno por su cuenta puede dar "25 h 60 min"
  //  (59.99 min de la parte fraccionaria redondeando a 60) en vez de
  //  arrastrar ese minuto a la hora siguiente.
  const minutosTotales = Math.round(horas * 60);
  if (minutosTotales < 60) return `Offline válido durante ${String(minutosTotales)} min`;
  const h = Math.floor(minutosTotales / 60);
  const m = minutosTotales % 60;
  return `Offline válido durante ${String(h)} h ${String(m)} min`;
}

/**
 * FASE 5.7-C C03 — vocabulario en claro de cada tipo de mutación. Nunca se
 * enseña el nombre técnico (`LINEA_CONFIRMAR`, `NC_INICIAR_TRATAMIENTO`…) a
 * quien usa la aplicación.
 */
/** F14.1 — desde el registro común de operaciones (`offline/operaciones.ts`). */
const ETIQUETA_TIPO_MUTACION = (t: TipoMutacion): string => etiquetaOperacion(t);


/**
 * "Cambios pendientes (N) ▼" (encargo FASE 5.7-C C03): un único resumen
 * desplegable, en vez de que el usuario tenga que saber qué es "necesita
 * revisión" frente a "listo para sincronizar" por su cuenta. Reutiliza
 * ÍNTEGRAMENTE `mutacionesActivas`/`lineasPorId` que `PaginaOffline` ya lee
 * para los contadores — no construye ningún estado ni lectura paralela.
 * Nunca enseña `idOperacionCliente`/UUID/`revisionBase`/IndexedDB/AES: sólo
 * OT, línea (si aplica), tipo en claro y estado en claro.
 */
function ResumenCambiosPendientes({
  mutaciones,
  ots,
  lineasPorId,
  esEncargado,
  onRevisar,
}: {
  mutaciones: readonly Mutacion[];
  ots: readonly OtOfflineLocal[];
  lineasPorId: ReadonlyMap<string, LineaOfflineLocal>;
  esEncargado: boolean;
  onRevisar: () => void;
}): ReactNode {
  if (mutaciones.length === 0) return null;

  const otsPorId = new Map(ots.map((o) => [o.id, o] as const));
  const porTipo = new Map<TipoMutacion, number>();
  for (const m of mutaciones) porTipo.set(m.tipo, (porTipo.get(m.tipo) ?? 0) + 1);

  const pendientesDeEnviar = mutaciones.filter((m) => m.estadoSync === 'PENDIENTE' || m.estadoSync === 'SINCRONIZANDO').length;
  const necesitanRevision = mutaciones.filter((m) => m.estadoSync === 'EN_ESPERA' || m.estadoSync === 'RECHAZADA').length;
  const conError = mutaciones.filter((m) => m.estadoSync === 'ERROR_REINTENTABLE').length;

  function lineaDe(m: Mutacion): LineaOfflineLocal | undefined {
    if (esMutacionDeLinea(m)) return lineasPorId.get(m.idEntidad);
    if (m.tipo === 'EVIDENCIA_SUBIR' && (m.payload as { ambito: string }).ambito === 'LINEA') return lineasPorId.get(m.idEntidad);
    return undefined;
  }

  return (
    <details className="panel sep">
      <summary className="panel-cabecera" style={{ cursor: 'pointer', listStyle: 'none' }}>
        <h2 style={{ display: 'inline' }}>Cambios pendientes ({mutaciones.length})</h2>
      </summary>
      <div className="panel-cuerpo">
        <div className="datos">
          {[...porTipo.entries()].map(([tipo, n]) => (
            <div key={tipo}>
              <div className="etiqueta-campo">{ETIQUETA_TIPO_MUTACION(tipo)}</div>
              <div>{n}</div>
            </div>
          ))}
        </div>

        <div className="fila" style={{ gap: 14, marginTop: 14, flexWrap: 'wrap' }}>
          {pendientesDeEnviar > 0 && (
            <span className="etiqueta neutra">{pendientesDeEnviar} pendiente{pendientesDeEnviar === 1 ? '' : 's'} de enviar</span>
          )}
          {conError > 0 && <span className="etiqueta error">{conError} con error — se reintentará</span>}
          {necesitanRevision > 0 && esEncargado && (
            <span className="fila" style={{ gap: 8 }}>
              <span className="etiqueta aviso">{necesitanRevision} necesita{necesitanRevision === 1 ? '' : 'n'} revisión</span>
              <button type="button" className="pequeno" onClick={onRevisar}>Revisar</button>
            </span>
          )}
        </div>

        <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '14px 0 0', padding: 0, listStyle: 'none' }}>
          {mutaciones.map((m) => {
            const ot = otsPorId.get(m.idOt);
            const linea = lineaDe(m);
            const estado = presentarEstado(m);
            return (
              <li
                key={m.idOperacionCliente}
                className="fila separa"
                style={{ flexWrap: 'wrap', borderTop: '1px solid var(--borde)', paddingTop: 8 }}
              >
                <span className="pequena" style={{ minWidth: 0 }}>
                  <strong>{ot?.codigo ?? 'OT no disponible'}</strong>
                  {linea !== undefined && (
                    <span className="suave"> · {linea.ubicacionCodigo} · {linea.tipoCodigo}</span>
                  )}
                  <span className="suave"> — {rotuloOperacion(m)}</span>
                </span>
                <span className={`etiqueta ${estado.clase} pequena`}>{estado.texto}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}

function PanelEstadoPaquete({
  meta,
  pendientes,
  preparando,
  mensaje,
  onPreparar,
}: {
  meta: MetaOffline | null;
  pendientes: number | null;
  preparando: boolean;
  mensaje: string | null;
  onPreparar: () => void;
}): ReactNode {
  const conectividad = useConectividad();
  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Datos sin conexión</h2>
      </div>
      <div className="panel-cuerpo">
        {meta === null ? (
          <p className="suave">Todavía no has preparado datos para trabajar sin conexión en este dispositivo.</p>
        ) : (
          <div className="datos">
            <div>
              <div className="etiqueta-campo">Última actualización</div>
              <div>{fechaHora(meta.ultimaSincronizacionEn)}</div>
            </div>
            <div>
              <div className="etiqueta-campo">Validez</div>
              <div>{textoValidez(meta)}</div>
            </div>
            <div>
              <div className="etiqueta-campo">{meta.rol === 'TECNICO' ? 'Partes de trabajo disponibles' : 'Órdenes de trabajo disponibles'}</div>
              <div>{meta.totalOts}</div>
            </div>
            {pendientes !== null && pendientes > 0 && (
              <div>
                <div className="etiqueta-campo">Cambios pendientes</div>
                <div>{pendientes}</div>
              </div>
            )}
          </div>
        )}
        <div className="fila" style={{ marginTop: 14, gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            className="principal"
            disabled={preparando || conectividad === 'OFFLINE'}
            onClick={onPreparar}
          >
            {preparando ? 'Preparando…' : 'Preparar datos para trabajar sin conexión'}
          </button>
          {conectividad === 'OFFLINE' && (
            <span className="suave pequena">Sin conexión: no se puede actualizar el paquete ahora.</span>
          )}
        </div>
        {mensaje !== null && <p className="nota" style={{ marginTop: 10 }}>{mensaje}</p>}
      </div>
    </div>
  );
}

/**
 * Sincronización manual (§15) + estado global (§22). Sólo se pinta para
 * ENCARGADO — OPERARIO nunca crea mutaciones, así que su cola
 * sincronizable siempre está vacía y el botón no aportaría nada salvo
 * confusión.
 */
function PanelSincronizacion({
  idPerfil,
  sincronizables,
  onTrasSincronizar,
}: {
  idPerfil: string;
  sincronizables: number;
  onTrasSincronizar: () => void;
}): ReactNode {
  const conectividad = useConectividad();
  const estadoSync = useEstadoSync();
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [deOtroUsuario, setDeOtroUsuario] = useState(false);

  if (sincronizables === 0 && estadoSync === 'INACTIVO') return null;

  async function sincronizarAhora(): Promise<void> {
    setMensaje(null);
    setDeOtroUsuario(false);
    const resumen = await sincronizarPendientes(idPerfil);
    onTrasSincronizar();
    if (resumen === null) {
      //  F14.2 — el perfil desbloqueado no es el del usuario de la sesión: no se ha enviado nada.
      if (ultimaSincronizacionSinIdentidad() === 'OTRO_USUARIO') {
        setDeOtroUsuario(true);
        return;
      }
      //  FASE 5.7-B UAT-Q02 — una sesión caducada (401) y un fallo de red
      //  puro se tratan IGUAL en los datos (nunca se toca ni una
      //  mutación, en ningún caso — ver `sync.ts::ejecutarSincronizacion`)
      //  pero merecen un mensaje distinto: "sin conexión" es engañoso
      //  cuando el problema real es que hace falta volver a iniciar
      //  sesión. Los pendientes siguen intactos en cualquiera de los dos
      //  casos — sólo cambia qué le dice esta pantalla al usuario.
      if (ultimaSincronizacionFueSesionCaducada()) {
        setMensaje('Tu sesión ha caducado. Vuelve a iniciar sesión para sincronizar — los cambios pendientes no se han perdido.');
      } else {
        setMensaje(conectividad === 'OFFLINE' ? null : 'No se ha podido contactar con el servidor. Se reintentará más adelante.');
      }
      return;
    }
    const partes: string[] = [];
    if (resumen.aplicadas + resumen.yaAplicadas > 0) partes.push(`${String(resumen.aplicadas + resumen.yaAplicadas)} sincronizado(s)`);
    if (resumen.conConflicto + resumen.omitidas > 0) partes.push(`${String(resumen.conConflicto + resumen.omitidas)} necesitan revisión`);
    if (resumen.rechazadas > 0) partes.push(`${String(resumen.rechazadas)} no se pudieron aplicar`);
    if (resumen.errorTemporal > 0) partes.push(`${String(resumen.errorTemporal)} con error temporal`);
    setMensaje(partes.length === 0 ? 'Sincronización completada.' : partes.join(' · '));
  }

  return (
    <div className="nota" role="status" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="fila" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {estadoSync === 'SINCRONIZANDO' ? (
          <span>Sincronizando…</span>
        ) : (
          <>
            {sincronizables > 0 && (
              <span>
                {sincronizables} {sincronizables === 1 ? 'cambio listo' : 'cambios listos'} para sincronizar
              </span>
            )}
            {sincronizables > 0 && conectividad === 'ONLINE' && (
              <button type="button" className="pequeno" onClick={() => void sincronizarAhora()}>
                Sincronizar ahora
              </button>
            )}
            {sincronizables > 0 && conectividad === 'OFFLINE' && (
              <span className="suave pequena">Sin conexión: se sincronizará automáticamente al reconectar.</span>
            )}
          </>
        )}
      </div>
      {mensaje !== null && <p className="suave pequena" style={{ margin: 0 }}>{mensaje}</p>}
      {deOtroUsuario && <AvisoPerfilDeOtroUsuario />}
    </div>
  );
}

export function PaginaOffline(): ReactNode {
  const { perfilId, roles } = useSesionOfflineActiva();
  const esEncargado = roles.some((r) => r.rol === 'ENCARGADO');
  const navegar = useNavigate();
  const location = useLocation();
  const [meta, setMeta] = useState<MetaOffline | null>(null);
  const [ots, setOts] = useState<OtOfflineLocal[] | null>(null);
  //  FASE 5.7-C C03 — única fuente de la que se derivan TODOS los
  //  contadores de abajo (`pendientes`/`sincronizables`/`necesitanRevision`)
  //  y el resumen desplegable "Cambios pendientes": antes eran cuatro
  //  lecturas de IndexedDB independientes que podían, en teoría, desincronizarse
  //  entre sí (cada una leyendo el almacén en un instante distinto).
  const [mutacionesActivas, setMutacionesActivas] = useState<Mutacion[]>([]);
  //  Código de ubicación+tipo de cada línea referenciada por una mutación
  //  activa — sólo para el desglose de C03 ("línea, si aplica"); nunca se
  //  usa para decidir nada, es puramente presentacional.
  const [lineasPorId, setLineasPorId] = useState<ReadonlyMap<string, LineaOfflineLocal>>(new Map());
  const pendientes = mutacionesActivas.filter((m) => m.estadoSync === 'PENDIENTE' || m.estadoSync === 'SINCRONIZANDO').length;
  const sincronizables = mutacionesActivas.filter(esMutacionActiva).length;
  const [preparando, setPreparando] = useState(false);
  //  F14.2 — sólo se lleva al OPERARIO a «Mis trabajos» cuando llega aquí por el ARRANQUE SIN CONEXIÓN
  //  (`RequiereSesion`, `state.arranqueSinConexion`), y una sola vez: si prepara o gestiona perfiles, se queda.
  //  Navegar a `/offline` a mano nunca le saca de aquí.
  const [desdeArranqueSinConexion, setDesdeArranqueSinConexion] = useState(
    (location.state as { arranqueSinConexion?: boolean } | null)?.arranqueSinConexion === true,
  );
  //  FASE 5.7-C C02 — si se llega aquí por la redirección automática desde
  //  el menú normal de OT (sin conexión), `avisoOffline` explica por qué
  //  el usuario no ha aterrizado donde pulsó. Mismo estado `mensaje` que ya
  //  pinta `PanelEstadoPaquete` para "Preparar datos" — no se inventa un
  //  segundo cartel para lo mismo.
  const [mensaje, setMensaje] = useState<string | null>(
    (location.state as { avisoOffline?: string } | null)?.avisoOffline ?? null,
  );
  const [gestionandoPerfil, setGestionandoPerfil] = useState(false);
  const [errorLectura, setErrorLectura] = useState<string | null>(null);

  const recargarLocal = useCallback(async (idPerfil: string) => {
    try {
      const [m, listado, activas] = await Promise.all([
        estadoLocal(idPerfil),
        listarOtsLocal(idPerfil),
        listarMutacionesNoSincronizadas(idPerfil),
      ]);

      //  FASE 5.7-C C03 — códigos de línea para el desglose por ítem:
      //  sólo se resuelven las líneas de verdad referenciadas (nunca todas
      //  las de todas las OT), y sólo cuando la mutación es "de línea" o
      //  una evidencia adjunta a una línea concreta.
      const idsLinea = new Set<string>();
      for (const mut of activas) {
        if (esMutacionDeLinea(mut)) idsLinea.add(mut.idEntidad);
        else if (mut.tipo === 'EVIDENCIA_SUBIR' && (mut.payload as { ambito: string }).ambito === 'LINEA') {
          idsLinea.add(mut.idEntidad);
        }
      }
      const lineas = await Promise.all([...idsLinea].map((id) => leerLineaLocal(idPerfil, id)));
      const mapaLineas = new Map<string, LineaOfflineLocal>();
      lineas.forEach((linea, i) => {
        if (linea !== null) mapaLineas.set([...idsLinea][i]!, linea);
      });

      setErrorLectura(null);
      setMeta(m);
      listado.sort((a, b) => a.inicioProgramadoEn.localeCompare(b.inicioProgramadoEn));
      setOts(listado);
      setMutacionesActivas(activas);
      setLineasPorId(mapaLineas);
    } catch {
      setErrorLectura(MENSAJE_FALLO_LECTURA_LOCAL);
    }
  }, []);

  useEffect(() => {
    void recargarLocal(perfilId);
  }, [perfilId, recargarLocal]);

  //  F14.1 — la cola también cambia sin pasar por esta pantalla (la sincronización automática al reconectar o al
  //  desbloquear, otra pestaña de la SPA): sin esto, «N cambios listos para sincronizar» seguía en pantalla con
  //  la cola ya sincronizada. Se agrupan los avisos de una misma ronda en una sola relectura.
  useEffect(() => {
    let pendiente: ReturnType<typeof setTimeout> | null = null;
    const cancelar = suscribirCambiosCola(() => {
      if (pendiente !== null) return;
      pendiente = setTimeout(() => {
        pendiente = null;
        void recargarLocal(perfilId);
      }, 150);
    });
    return () => {
      cancelar();
      if (pendiente !== null) clearTimeout(pendiente);
    };
  }, [perfilId, recargarLocal]);

  //  El disparo automático (§16) se activa una única vez por sesión desde
  //  `contexto.tsx` (`Proveedores`), no aquí: esta pantalla se monta y
  //  desmonta cada vez que se navega a `/offline` dentro de la SPA, y eso
  //  NUNCA debe confundirse con "se reabrió la aplicación". Esta pantalla
  //  sólo refleja el resultado (recargando tras cada sincronización) y
  //  ofrece el disparo MANUAL (§15).
  const alTerminarSincronizacion = useCallback(() => {
    void recargarLocal(perfilId);
  }, [perfilId, recargarLocal]);

  async function preparar(): Promise<void> {
    setPreparando(true);
    setDesdeArranqueSinConexion(false);
    setMensaje(null);
    try {
      const r = await prepararPaqueteOffline(perfilId);
      //  F14.3 — el paquete del técnico trae partes, no OT.
      const partes = [r.paquete.mantenimiento != null ? `${String(r.paquete.mantenimiento.partes.length)} partes disponibles.` : `${String(r.paquete.ots.length)} OT disponibles.`];
      if (r.documentosDescargados > 0) partes.push(`${String(r.documentosDescargados)} documento(s) descargado(s).`);
      if (r.documentosFallidos > 0) {
        partes.push(`${String(r.documentosFallidos)} documento(s) no se pudieron descargar — se reintentará la próxima vez.`);
      }
      setMensaje(partes.join(' '));
      await recargarLocal(perfilId);
    } catch (e) {
      setMensaje(e instanceof Error ? `No se ha podido preparar el paquete: ${e.message}` : 'No se ha podido preparar el paquete.');
    } finally {
      setPreparando(false);
    }
  }

  if (!tieneCapacidadOffline(roles)) {
    return (
      <Shell titulo="Sin conexión">
        <Vacio titulo="El acceso offline no está disponible para tu perfil" />
      </Shell>
    );
  }

  //  F14.2 — el OPERARIO trabaja siempre en «Mis trabajos» (con o sin conexión); esta pantalla es la de preparar y
  //  gestionar el dispositivo. Arrancando sin conexión con datos ya preparados, se le lleva a su jornada.
  const esOperarioLocal = !esEncargado && roles.some((r) => r.rol === 'OPERARIO');
  //  F14.3 — lo mismo para el TÉCNICO, en «Mis partes».
  const esTecnicoLocal = !esEncargado && roles.some((r) => r.rol === 'TECNICO_MANTENIMIENTO');
  if (desdeArranqueSinConexion && esOperarioLocal && meta !== null && !gestionandoPerfil) {
    return <Navigate to="/mis-ots" replace />;
  }
  if (desdeArranqueSinConexion && esTecnicoLocal && meta !== null && !gestionandoPerfil) {
    return <Navigate to="/mantenimiento/mis-partes" replace />;
  }

  if (gestionandoPerfil) {
    return (
      <Shell titulo="Trabajar sin conexión">
        <PanelGestionPerfilOffline
          perfilId={perfilId}
          aliasLocal={perfilActivo()?.aliasLocal ?? ''}
          pendientes={pendientes ?? 0}
          alCambiarPerfil={() => setGestionandoPerfil(false)}
        />
      </Shell>
    );
  }

  return (
    <Shell titulo="Trabajar sin conexión">
      <PanelEstadoPaquete
        meta={meta}
        pendientes={pendientes}
        preparando={preparando}
        mensaje={mensaje}
        onPreparar={() => void preparar()}
      />

      {esOperarioLocal && meta !== null && (
        <div className="fila" style={{ gap: 8 }}>
          <Link to="/mis-ots" className="boton principal" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>Ir a Mis trabajos</Link>
        </div>
      )}
      {esTecnicoLocal && meta !== null && (
        <div className="fila" style={{ gap: 8 }}>
          <Link to="/mantenimiento/mis-partes" className="boton principal" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>Ir a Mis partes</Link>
        </div>
      )}

      <ResumenCambiosPendientes
        mutaciones={mutacionesActivas}
        ots={ots ?? []}
        lineasPorId={lineasPorId}
        esEncargado={esEncargado || esOperarioLocal || esTecnicoLocal}
        onRevisar={() => navegar('/offline/revision')}
      />

      <div className="fila" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="enlace" onClick={() => { setDesdeArranqueSinConexion(false); setGestionandoPerfil(true); }}>
          Este dispositivo
        </button>
      </div>

      {esEncargado && (
        <PanelSincronizacion
          idPerfil={perfilId}
          sincronizables={sincronizables}
          onTrasSincronizar={alTerminarSincronizacion}
        />
      )}

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Órdenes de trabajo disponibles offline</h2>
        </div>
        <div className="panel-cuerpo sin-relleno">
          {errorLectura !== null ? (
            <Vacio titulo={errorLectura} />
          ) : ots === null ? (
            <Cargando />
          ) : ots.length === 0 ? (
            <Vacio titulo="Sin datos locales todavía" />
          ) : (
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 16, listStyle: 'none' }}>
              {ots.map((ot) => (
                <li key={ot.id} className="tarjeta-ot">
                  <div className="tarjeta-ot-cabecera">
                    <Link to={esOperarioLocal ? `/mis-ots/${ot.id}` : `/offline/${ot.id}`} className="tarjeta-ot-codigo">{ot.codigo}</Link>
                    <EstadoOt estado={ot.estado} />
                  </div>
                  <div className="tarjeta-ot-datos">
                    <div className="tarjeta-ot-fecha">{fechaHora(ot.inicioProgramadoEn)}</div>
                    <div className="suave pequena">
                      {ot.centroDescripcion} <span className="cod">{ot.centroCodigo}</span>
                      {ot.fueraDeVentana === true && <span className="etiqueta aviso" style={{ marginLeft: 6 }}>Fuera de ventana</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Shell>
  );
}

export function BotonAbrirDocumentoLocal({ idPerfil, doc }: { idPerfil: string; doc: DocumentoOfflineLocal }): ReactNode {
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);

  async function abrir(): Promise<void> {
    setCargando(true);
    setFallo(false);
    const blob = await leerBlobDocumento(idPerfil, doc.hashSha256);
    setCargando(false);
    if (blob === null) {
      setFallo(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    //  Sin `noopener`, a diferencia de `documentos-aplicables.tsx`/
    //  `evidencias.tsx`: un blob: creado con `URL.createObjectURL` sólo
    //  es resoluble en un proceso de renderizado con acceso al realm que
    //  lo creó, y `noopener` puede forzar a Chromium a abrir la pestaña
    //  en un proceso aislado sin ese acceso — la pestaña se queda en
    //  blanco, sin navegar nunca (comprobado). El PDF ya es un blob local
    //  sin ninguna credencial ni referer que proteger, así que no hay
    //  nada que `noopener` estuviera defendiendo aquí.
    window.open(url, '_blank');
    registrarUrlTemporal(url);
    //  Además del registro global que `PuertaOfflineSegura` revoca al
    //  bloquear (§41), un límite de tiempo best-effort para la sesión
    //  desbloqueada normal — igual criterio que `documentos.tsx`/
    //  `evidencias.tsx`.
    setTimeout(() => revocarUrlTemporal(url), 60_000);
  }

  if (!doc.disponibleOffline) {
    return <span className="suave pequena">Necesitas conexión para descargar este documento.</span>;
  }
  return (
    <span className="fila" style={{ gap: 6, alignItems: 'center' }}>
      <button type="button" className="pequeno" disabled={cargando} onClick={() => void abrir()}>
        {cargando ? 'Abriendo…' : 'Abrir'}
      </button>
      {fallo && <span className="suave pequena">No se ha podido abrir.</span>}
    </span>
  );
}

const ETIQUETA_ACCION: Record<TipoMutacionLinea, string> = {
  LINEA_CONFIRMAR: 'Confirmar',
  LINEA_VERIFICAR: 'Verificar',
  LINEA_NO_OK: 'No OK',
  LINEA_NO_APLICA: 'No aplica',
};

const ETIQUETA_TIPO_NC: Record<TipoMutacionNc, string> = {
  NC_CREAR: 'Abrir no conformidad',
  NC_INICIAR_TRATAMIENTO: 'Iniciar tratamiento',
  NC_CERRAR: 'Cerrar NC',
};

const ETIQUETA_GRAVEDAD_NC: Record<'LEVE' | 'GRAVE' | 'CRITICA', string> = {
  LEVE: 'Leve',
  GRAVE: 'Grave',
  CRITICA: 'Crítica',
};

/** F14.1 — el mismo vocabulario en toda la aplicación (`offline/estados-sync.ts`). */
const textoEstado = (m: Pick<Mutacion, 'estadoSync' | 'razonEspera' | 'intentos'>): string => presentarEstado(m).texto;

const CONTEXTOS_OT_LINEA_OFFLINE = ['GENERAL', 'ANTES', 'DESPUES', 'RESULTADO'] as const;
const CONTEXTOS_NC_OFFLINE = ['APERTURA_NC', 'TRATAMIENTO_NC', 'RESOLUCION_NC'] as const;

type DestinoEvidenciaOffline =
  | { readonly ambito: 'OT' }
  | { readonly ambito: 'LINEA'; readonly idLinea: string }
  | { readonly ambito: 'NC'; readonly idOperacionClienteNc: string };

/**
 * Formulario de evidencia offline, compartido por los tres ámbitos (OT,
 * línea, NC) — mismo criterio que el `Evidencias` online (encargo §27:
 * "reutilizar patrón existente"), adaptado para escribir en la cola local
 * (`crearMutacionEvidencia`) en vez de subir directamente.
 */
function FormularioEvidenciaOffline({
  idPerfil,
  roles,
  idOt,
  destino,
  onGuardado,
  onCancelar,
}: {
  idPerfil: string;
  roles: readonly { rol: string }[];
  idOt: string;
  destino: DestinoEvidenciaOffline;
  onGuardado: () => void;
  onCancelar: () => void;
}): ReactNode {
  const contextos = destino.ambito === 'NC' ? CONTEXTOS_NC_OFFLINE : CONTEXTOS_OT_LINEA_OFFLINE;
  const [archivo, setArchivo] = useState<File | null>(null);
  const [contexto, setContexto] = useState<string>(contextos[0]);
  const [descripcion, setDescripcion] = useState('');
  const [tomadaEn, setTomadaEn] = useState('');
  const [visibilidad, setVisibilidad] = useState<'INTERNA' | 'CLIENTE'>('INTERNA');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar(): Promise<void> {
    if (archivo === null) {
      setError('Elige un fichero para adjuntar.');
      return;
    }
    setGuardando(true);
    setError(null);
    const fechaEfectiva = new Date().toISOString();
    let tomadaEnIso: string | null = null;
    if (tomadaEn.trim() !== '') {
      const d = new Date(tomadaEn);
      if (!Number.isNaN(d.getTime())) tomadaEnIso = d.toISOString();
    }
    const comun = {
      idOt,
      contexto,
      descripcion: descripcion.trim() === '' ? null : descripcion.trim(),
      tomadaEn: tomadaEnIso,
      visibilidad,
      archivo,
      nombreArchivo: archivo.name,
      fechaEfectiva,
    };
    const datos: DatosNuevaMutacionEvidencia =
      destino.ambito === 'OT'
        ? { ...comun, ambito: 'OT' }
        : destino.ambito === 'LINEA'
          ? { ...comun, ambito: 'LINEA', idLinea: destino.idLinea }
          : { ...comun, ambito: 'NC', idOperacionClienteNc: destino.idOperacionClienteNc };
    const r = await crearMutacionEvidencia(idPerfil, roles, datos);
    setGuardando(false);
    if (!r.ok) {
      setError(r.motivo);
      return;
    }
    onGuardado();
  }

  return (
    //  FASE 5.7-C C05 — `min(420px, 90vw)`, nunca un `420` fijo NI un
    //  `min(420px, 100%)`: este formulario puede montarse dentro de la
    //  celda `colSpan` de `.tabla-lineas-offline` (adjuntar evidencia por
    //  línea), y esa celda hereda el ancho TOTAL de la tabla (596px de
    //  contenido real a 390px de viewport) — un `100%` ahí sigue
    //  resolviendo a 596px, no a lo que de verdad cabe en pantalla. `vw`
    //  ata el límite al viewport real, sea cual sea el ancho del
    //  contenedor donde se monte. Nunca desbordaba la PÁGINA
    //  (`overflow-x:hidden` en `html,body` lo disimulaba), pero sí dejaba
    //  el formulario fuera del área visible.
    <div className="datos" style={{ maxWidth: 'min(420px, 90vw)' }}>
      <h4 style={{ margin: 0 }}>Adjuntar evidencia</h4>
      <div className="campo">
        <label htmlFor="evidencia-offline-archivo">Fichero</label>
        <input
          id="evidencia-offline-archivo"
          type="file"
          accept="image/*"
          onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
        />
      </div>
      <div className="campo">
        <label htmlFor="evidencia-offline-contexto">Contexto</label>
        <select id="evidencia-offline-contexto" value={contexto} onChange={(e) => setContexto(e.target.value)}>
          {contextos.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div className="campo">
        <label htmlFor="evidencia-offline-descripcion">Descripción (opcional)</label>
        <textarea id="evidencia-offline-descripcion" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="evidencia-offline-tomada-en">Tomada el (opcional)</label>
        <input
          id="evidencia-offline-tomada-en"
          type="datetime-local"
          value={tomadaEn}
          onChange={(e) => setTomadaEn(e.target.value)}
        />
      </div>
      <div className="campo">
        <label htmlFor="evidencia-offline-visibilidad">Visibilidad</label>
        <select
          id="evidencia-offline-visibilidad"
          value={visibilidad}
          onChange={(e) => setVisibilidad(e.target.value === 'CLIENTE' ? 'CLIENTE' : 'INTERNA')}
        >
          <option value="INTERNA">Interna</option>
          <option value="CLIENTE">Cliente</option>
        </select>
      </div>
      {error !== null && <p className="nota error" role="alert">{error}</p>}
      <div className="fila" style={{ gap: 8 }}>
        <button type="button" className="principal" disabled={guardando} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Adjuntar evidencia'}
        </button>
        <button type="button" className="pequeno" onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

/**
 * Una línea del detalle offline, con sus acciones (ENCARGADO) y su cola
 * pendiente. Cada fila lleva su propia mini-cola: `listarMutacionesDeLinea`
 * + `proyectarEstadoLinea` deciden qué mostrar como estado y qué acciones
 * ofrecer — nunca el `estado` crudo del snapshot a secas.
 */
function LineaOfflineFila({
  idPerfil,
  roles,
  idOt,
  linea,
  esEncargado,
  ttlVencido,
  mostrarCasillaSeleccion = false,
  seleccionable = false,
  seleccionada = false,
  onAlternarSeleccion,
  onCambio,
}: {
  idPerfil: string;
  roles: readonly { rol: string }[];
  idOt: string;
  linea: LineaOfflineLocal;
  esEncargado: boolean;
  ttlVencido: boolean;
  /** FASE 5.7-C C06 — sólo se pinta la columna/casilla cuando AL MENOS una línea de la OT admite alguna acción masiva; nunca una columna vacía "por si acaso". */
  mostrarCasillaSeleccion?: boolean;
  /** Igual criterio EXACTO que decide los botones de acción individual (ver `accionesMasivasDe` en `PaginaOfflineOt`) — nunca una línea seleccionable para algo que su propia fila no ofrecería. */
  seleccionable?: boolean;
  seleccionada?: boolean;
  onAlternarSeleccion?: () => void;
  onCambio: () => void;
}): ReactNode {
  const [mutaciones, setMutaciones] = useState<Mutacion[] | null>(null);
  const [accionAbierta, setAccionAbierta] = useState<TipoMutacionLinea | null>(null);
  const [observacion, setObservacion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ejecutores, setEjecutores] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evidenciaAbierta, setEvidenciaAbierta] = useState(false);

  const cargarMutaciones = useCallback(async () => {
    //  FASE 5.6 §16: si el perfil se bloquea a mitad de la lectura (logout, bloqueo manual), se aborta en silencio;
    //  nunca un rechazo sin capturar. El siguiente desbloqueo vuelve a pintar la línea.
    try {
      setMutaciones(await listarMutacionesDeLinea(idPerfil, linea.id));
    } catch (e) {
      if (e instanceof ErrorPerfilBloqueado) return;
      throw e;
    }
  }, [idPerfil, linea.id]);

  useEffect(() => {
    void cargarMutaciones();
  }, [cargarMutaciones]);

  //  `listarMutacionesDeLinea` indexa por `idEntidad`: una `EVIDENCIA_SUBIR`
  //  con ámbito LINEA sobre esta MISMA línea comparte ese índice sin
  //  participar de su máquina de estados — se muestra aparte, más abajo.
  const mutacionesLinea = (mutaciones ?? []).filter(esMutacionDeLinea);
  const mutacionesEvidencia = (mutaciones ?? []).filter((m) => m.tipo === 'EVIDENCIA_SUBIR');

  //  Lo que proyecta el estado local es lo ACTIVO (PENDIENTE +
  //  ERROR_REINTENTABLE — FASE 5.3 §14, `esMutacionActiva`), nunca sólo
  //  PENDIENTE: un fallo transitorio de sincronización no resuelve nada.
  const activas = mutacionesLinea.filter(esMutacionActiva);
  const estadoProyectado = mutaciones === null ? linea.estado : proyectarEstadoLinea(linea.estado, activas);
  const disponibles = esEncargado && !ttlVencido && !(linea.fueraDeVentana ?? false)
    ? accionesLocalesDisponibles(estadoProyectado)
    : [];
  //  Todo lo que el servidor aún no ha resuelto como SINCRONIZADA — la
  //  mini-cola de esta línea (§22): PENDIENTE, EN_ESPERA, RECHAZADA y
  //  ERROR_REINTENTABLE, todas a la vez, cada una con su propia etiqueta.
  const noSincronizadas = mutacionesLinea.filter((m) => m.estadoSync !== 'SINCRONIZADA');
  const masReciente = noSincronizadas.at(-1) ?? null;
  const evidenciasNoSincronizadas = mutacionesEvidencia.filter((m) => m.estadoSync !== 'SINCRONIZADA');

  function abrirAccion(tipo: TipoMutacionLinea): void {
    setAccionAbierta(tipo);
    setObservacion('');
    setMotivo('');
    //  FASE 5.7-C C04 — ASIGNADO (planificación, §10) nunca es lo mismo que
    //  EJECUTOR REAL (§30/quien de verdad hizo la tarea): esto sólo
    //  PRE-MARCA a los operarios asignados como punto de partida cómodo —
    //  el ENCARGADO sigue viendo cada casilla y puede desmarcar o añadir
    //  antes de "Registrar confirmar". Nunca se envía nada hasta que él lo
    //  confirma explícitamente; si no toca ninguna casilla, se registran
    //  los asignados TAL CUAL él los dejó marcados, no porque el código lo
    //  decidiera por su cuenta.
    setEjecutores(tipo === 'LINEA_CONFIRMAR' ? linea.asignados.map((a) => a.idOperario) : []);
    setError(null);
  }

  async function registrar(): Promise<void> {
    if (accionAbierta === null) return;
    if (accionAbierta === 'LINEA_CONFIRMAR' && ejecutores.length === 0) {
      setError('Selecciona al menos un ejecutor real.');
      return;
    }
    if ((accionAbierta === 'LINEA_NO_OK' || accionAbierta === 'LINEA_NO_APLICA') && motivo.trim() === '') {
      setError('El motivo es obligatorio.');
      return;
    }
    setGuardando(true);
    setError(null);
    const fechaEfectiva = new Date().toISOString();
    const obs = observacion.trim() === '' ? null : observacion.trim();
    const payload: PayloadMutacion =
      accionAbierta === 'LINEA_CONFIRMAR'
        ? { fechaEfectiva, observacion: obs, ejecutoresReales: ejecutores }
        : accionAbierta === 'LINEA_VERIFICAR'
          ? { fechaEfectiva, observacion: obs }
          : { fechaEfectiva, observacion: obs, motivo: motivo.trim() };

    const r = await crearMutacionLinea(idPerfil, roles, {
      tipo: accionAbierta,
      idOt,
      idLinea: linea.id,
      payload,
      fechaEfectiva,
    });
    setGuardando(false);
    if (!r.ok) {
      setError(r.motivo);
      return;
    }
    setAccionAbierta(null);
    await cargarMutaciones();
    onCambio();
  }

  async function descartar(m: Mutacion): Promise<void> {
    //  §24 — una RECHAZADA se descarta con su propio camino (nunca se
    //  reintenta ni se reescribe con el mismo UUID); una PENDIENTE sigue
    //  usando el de FASE 5.2. Nunca se ofrece descartar EN_ESPERA ni
    //  ERROR_REINTENTABLE: la primera exige una decisión explícita (fuera
    //  de alcance en 5.3), la segunda todavía tiene un camino hacia
    //  adelante (el próximo reintento).
    if (m.estadoSync === 'RECHAZADA') await descartarMutacionRechazada(idPerfil, m.idOperacionCliente);
    else await descartarMutacionPendiente(idPerfil, m.idOperacionCliente);
    await cargarMutaciones();
    onCambio();
  }

  const columnas = (esEncargado ? 6 : 5) + (mostrarCasillaSeleccion ? 1 : 0);

  return (
    <>
      <tr>
        {mostrarCasillaSeleccion && (
          <td className="col-check">
            {seleccionable && (
              <input
                type="checkbox"
                checked={seleccionada}
                onChange={onAlternarSeleccion}
                aria-label={`Seleccionar línea ${linea.ubicacionCodigo} · ${linea.tipoCodigo}`}
              />
            )}
          </td>
        )}
        {/*  F10.3: `data-etiqueta` deja que, en móvil, cada fila se lea como una tarjeta (estilos.css). */}
        <td data-etiqueta="Ubicación">{linea.ubicacionRuta}</td>
        <td data-etiqueta="Tipo">{linea.tipoCodigo}</td>
        <td data-etiqueta="Tarea">{linea.descripcionTarea ?? '—'}</td>
        <td data-etiqueta="Asignados">{linea.asignados.length === 0 ? '—' : linea.asignados.map((a) => a.codigo).join(', ')}</td>
        <td data-etiqueta="Estado">
          {/*  F10.3: el operario lee su tarea igual que en línea (Pendiente / Realizada / No conforme). */}
          {esEncargado ? <EstadoLinea estado={estadoProyectado} /> : (
            <span className={`etiqueta ${estadoTareaOperario(estadoProyectado).tono}`}>{estadoTareaOperario(estadoProyectado).texto}</span>
          )}
          {masReciente !== null && <div className="suave pequena">{textoEstado(masReciente)}</div>}
        </td>
        {esEncargado && (
          <td data-etiqueta="Acciones">
            <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
              {disponibles.map((tipo) => (
                <button key={tipo} type="button" className="pequeno" onClick={() => abrirAccion(tipo)}>
                  {ETIQUETA_ACCION[tipo]}
                </button>
              ))}
              {!ttlVencido && !evidenciaAbierta && (
                <button type="button" className="pequeno" onClick={() => setEvidenciaAbierta(true)}>
                  Adjuntar evidencia
                </button>
              )}
              {disponibles.length === 0 && ttlVencido && <span className="suave pequena">—</span>}
            </div>
          </td>
        )}
      </tr>

      {noSincronizadas.length > 0 && (
        <tr>
          <td colSpan={columnas}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {noSincronizadas.map((m) => (
                <li key={m.idOperacionCliente} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div className="fila" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="pequena">
                      {ETIQUETA_ACCION[m.tipo]} · {textoEstado(m).toLowerCase()}
                    </span>
                    {esEncargado && (m.estadoSync === 'PENDIENTE' || m.estadoSync === 'RECHAZADA') && (
                      <button type="button" className="pequeno" onClick={() => void descartar(m)}>
                        {m.estadoSync === 'RECHAZADA' ? 'Descartar' : 'Descartar cambio pendiente'}
                      </button>
                    )}
                  </div>
                  {/* §23 — CONFLICTO: mínimo exigido, nunca la consola completa de resolución (eso es fase posterior) ni un botón de "forzar". */}
                  {m.detalleConflicto != null && (
                    <p className="suave pequena" style={{ margin: 0 }}>
                      Esta acción necesita revisión porque la línea cambió mientras el dispositivo estaba sin conexión.
                      {' '}Acción intentada: {ETIQUETA_ACCION[m.tipo]} · Fecha efectiva: {fechaHora(m.fechaEfectiva)} · Estado actual en el servidor:{' '}
                      {m.detalleConflicto.estadoServidorActual}.
                    </p>
                  )}
                  {m.detalleConflicto == null && m.ultimoError !== null && (m.estadoSync === 'EN_ESPERA' || m.estadoSync === 'RECHAZADA') && (
                    <p className="suave pequena" style={{ margin: 0 }}>{m.ultimoError.mensaje}</p>
                  )}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}

      {evidenciasNoSincronizadas.length > 0 && (
        <tr>
          <td colSpan={columnas}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {evidenciasNoSincronizadas.map((m) => (
                <li key={m.idOperacionCliente} className="pequena">
                  Evidencia «{(m.payload as { nombreArchivo: string }).nombreArchivo}» · {textoEstado(m).toLowerCase()}
                  {m.ultimoError !== null && (m.estadoSync === 'RECHAZADA' || m.estadoSync === 'EN_ESPERA') && (
                    <span className="suave"> — {m.ultimoError.mensaje}</span>
                  )}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}

      {evidenciaAbierta && (
        <tr>
          <td colSpan={columnas}>
            <FormularioEvidenciaOffline
              idPerfil={idPerfil}
              roles={roles}
              idOt={idOt}
              destino={{ ambito: 'LINEA', idLinea: linea.id }}
              onGuardado={() => {
                setEvidenciaAbierta(false);
                void cargarMutaciones();
                onCambio();
              }}
              onCancelar={() => setEvidenciaAbierta(false)}
            />
          </td>
        </tr>
      )}

      {accionAbierta !== null && (
        <tr>
          <td colSpan={columnas}>
            {/* FASE 5.7-C C05 — ver comentario en `FormularioEvidenciaOffline`: mismo `min(420px, 90vw)`, mismo motivo. */}
            <div className="datos" style={{ maxWidth: 'min(420px, 90vw)' }}>
              <h3 style={{ margin: 0 }}>{ETIQUETA_ACCION[accionAbierta]}</h3>
              {accionAbierta === 'LINEA_CONFIRMAR' && (
                <div className="campo">
                  <label>Ejecutores reales</label>
                  {linea.asignados.length === 0 ? (
                    <p className="suave pequena">Sin operarios asignados en el paquete local.</p>
                  ) : (
                    <>
                      <p className="suave pequena" style={{ margin: '0 0 4px' }}>
                        Vienen marcados los operarios asignados — desmarca o añade quien haya ejecutado de verdad.
                      </p>
                      {linea.asignados.map((a) => (
                        <label key={a.idOperario} className="fila" style={{ gap: 6, alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={ejecutores.includes(a.idOperario)}
                            onChange={(e) => {
                              setEjecutores((prev) => (e.target.checked ? [...prev, a.idOperario] : prev.filter((id) => id !== a.idOperario)));
                            }}
                          />
                          {a.codigo}
                        </label>
                      ))}
                    </>
                  )}
                </div>
              )}
              {(accionAbierta === 'LINEA_NO_OK' || accionAbierta === 'LINEA_NO_APLICA') && (
                <div className="campo">
                  <label htmlFor={`motivo-${linea.id}`}>Motivo</label>
                  <textarea id={`motivo-${linea.id}`} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                </div>
              )}
              <div className="campo">
                <label htmlFor={`obs-${linea.id}`}>Observaciones (opcional)</label>
                <textarea id={`obs-${linea.id}`} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
              </div>
              {error !== null && <p className="nota error" role="alert">{error}</p>}
              <div className="fila" style={{ gap: 8 }}>
                <button type="button" className="principal" disabled={guardando} onClick={() => void registrar()}>
                  {guardando ? 'Guardando…' : `Registrar ${ETIQUETA_ACCION[accionAbierta].toLowerCase()}`}
                </button>
                <button type="button" className="pequeno" onClick={() => setAccionAbierta(null)}>Cancelar</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Resultado de UNA línea dentro de un lote — nunca se enseña `idOperacionCliente` (UUID); sólo un código y por qué. */
interface ResultadoLineaLote {
  readonly linea: LineaOfflineLocal;
  readonly ok: boolean;
  readonly motivo?: string;
}

function ResumenLoteMasivo({ resultados }: { resultados: readonly ResultadoLineaLote[] }): ReactNode {
  const ok = resultados.filter((r) => r.ok).length;
  const fallidas = resultados.filter((r) => !r.ok);
  return (
    <div className="datos" style={{ marginTop: 4 }}>
      <p role="status">
        {ok} de {resultados.length} {resultados.length === 1 ? 'línea' : 'líneas'} · {GUARDADO_EN_DISPOSITIVO.toLowerCase()}.
      </p>
      {fallidas.length > 0 && (
        <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {fallidas.map((r) => (
            <li key={r.linea.id} className="pequena suave">
              {r.linea.ubicacionCodigo} · {r.linea.tipoCodigo} — {r.motivo}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * FASE 5.7-C C07 — "Confirmar seleccionadas". CRÍTICO (encargo): esto NO
 * crea ningún tipo de mutación "masiva" nueva — llama a
 * `crearMutacionLinea` UNA VEZ POR LÍNEA, exactamente la misma función que
 * usa la confirmación individual (`LineaOfflineFila.registrar`). Cada
 * llamada obtiene su propio UUID, su propio `revisionBase` (copiado de
 * ESA línea en ESE instante) y su propia validación de transición — un
 * rechazo en una línea nunca aborta ni invalida las demás, porque no hay
 * ninguna operación conjunta que abortar: son N mutaciones independientes
 * desde el primer instante, antes incluso de llegar a sincronizar.
 *
 * Ejecutores reales: mismo principio que C04, por línea — se propone SU
 * PROPIA asignación, editable, nunca un ejecutor común para el lote. Una
 * línea sin ningún ejecutor marcado se omite del envío (mismo criterio que
 * `RevisionMultiOt`, la versión online): nunca se inventa uno para poder
 * mandarla.
 */
function PanelConfirmarSeleccionadas({
  idPerfil,
  roles,
  idOt,
  lineas,
  onCerrar,
  onTerminado,
}: {
  idPerfil: string;
  roles: readonly { rol: string }[];
  idOt: string;
  lineas: readonly LineaOfflineLocal[];
  onCerrar: () => void;
  onTerminado: () => void;
}): ReactNode {
  const [ejecutoresPorLinea, setEjecutoresPorLinea] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(lineas.map((l) => [l.id, l.asignados.map((a) => a.idOperario)])),
  );
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState<ResultadoLineaLote[] | null>(null);

  function alternarEjecutor(idLinea: string, idOperario: string, marcado: boolean): void {
    setEjecutoresPorLinea((prev) => {
      const actuales = prev[idLinea] ?? [];
      const siguientes = marcado ? [...actuales, idOperario] : actuales.filter((id) => id !== idOperario);
      return { ...prev, [idLinea]: siguientes };
    });
  }

  async function confirmarLote(): Promise<void> {
    setEnviando(true);
    const salida: ResultadoLineaLote[] = [];
    //  Secuencial a propósito: cada línea es independiente entre sí, pero
    //  el orden en que el ENCARGADO las ve en pantalla es el mismo orden
    //  en que quedan encoladas (`ordenLocal`) — nunca una carrera entre
    //  llamadas concurrentes por un beneficio de velocidad imperceptible.
    for (const linea of lineas) {
      const ejecutoresReales = ejecutoresPorLinea[linea.id] ?? [];
      if (ejecutoresReales.length === 0) {
        salida.push({ linea, ok: false, motivo: 'Sin ejecutores marcados — no se ha enviado.' });
        continue;
      }
      const fechaEfectiva = new Date().toISOString();
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await crearMutacionLinea(idPerfil, roles, {
          tipo: 'LINEA_CONFIRMAR',
          idOt,
          idLinea: linea.id,
          payload: { fechaEfectiva, observacion: null, ejecutoresReales },
          fechaEfectiva,
        });
        salida.push(r.ok ? { linea, ok: true } : { linea, ok: false, motivo: r.motivo });
      } catch {
        //  Un fallo INESPERADO (IndexedDB, no una validación normal) en
        //  UNA línea nunca debe dejar el lote a medias ni congelado en
        //  "Confirmando…" para siempre — se registra como fallida y se
        //  sigue con el resto, mismo principio que un rechazo normal.
        salida.push({ linea, ok: false, motivo: 'No se ha podido registrar (error inesperado).' });
      }
    }
    setResultados(salida);
    setEnviando(false);
  }

  if (resultados !== null) {
    return (
      <Modal titulo="Confirmar seleccionadas" cerrar={onTerminado} pie={<button type="button" className="principal" onClick={onTerminado}>Cerrar</button>}>
        <ResumenLoteMasivo resultados={resultados} />
      </Modal>
    );
  }

  if (lineas.length === 0) {
    return (
      <Modal titulo="Confirmar seleccionadas" cerrar={onCerrar}>
        <Vacio titulo="Ninguna de las líneas seleccionadas admite confirmar ahora" />
      </Modal>
    );
  }

  return (
    <Modal
      titulo="Confirmar seleccionadas"
      cerrar={onCerrar}
      pie={
        <div className="fila" style={{ gap: 8 }}>
          <button type="button" className="principal" disabled={enviando} onClick={() => void confirmarLote()}>
            {enviando ? 'Confirmando…' : `Confirmar ${String(lineas.length)} línea${lineas.length === 1 ? '' : 's'}`}
          </button>
          <button type="button" className="pequeno" disabled={enviando} onClick={onCerrar}>Cancelar</button>
        </div>
      }
    >
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {lineas.map((l) => (
          <li key={l.id} style={{ borderTop: '1px solid var(--borde)', paddingTop: 10 }}>
            <div className="pequena">
              <strong>{l.ubicacionCodigo}</strong> <span className="suave">· {l.tipoCodigo}</span>
            </div>
            {l.asignados.length === 0 ? (
              <p className="suave pequena" style={{ margin: '4px 0 0' }}>Sin operarios asignados en el paquete local.</p>
            ) : (
              <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                {l.asignados.map((a) => (
                  <label key={a.idOperario} className="fila pequena" style={{ gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={(ejecutoresPorLinea[l.id] ?? []).includes(a.idOperario)}
                      onChange={(e) => alternarEjecutor(l.id, a.idOperario, e.target.checked)}
                    />
                    {a.codigo}
                  </label>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Modal>
  );
}

/**
 * FASE 5.7-C C08 — "Verificar seleccionadas". Mismo principio que C07:
 * `crearMutacionLinea` una vez por línea, sin ejecutores (la verificación
 * no lleva ejecutor, igual que su formulario individual) — el servidor
 * sigue siendo la autoridad final de cada una al sincronizar.
 */
function PanelVerificarSeleccionadas({
  idPerfil,
  roles,
  idOt,
  lineas,
  onCerrar,
  onTerminado,
}: {
  idPerfil: string;
  roles: readonly { rol: string }[];
  idOt: string;
  lineas: readonly LineaOfflineLocal[];
  onCerrar: () => void;
  onTerminado: () => void;
}): ReactNode {
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState<ResultadoLineaLote[] | null>(null);

  async function verificarLote(): Promise<void> {
    setEnviando(true);
    const salida: ResultadoLineaLote[] = [];
    for (const linea of lineas) {
      const fechaEfectiva = new Date().toISOString();
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await crearMutacionLinea(idPerfil, roles, {
          tipo: 'LINEA_VERIFICAR',
          idOt,
          idLinea: linea.id,
          payload: { fechaEfectiva, observacion: null },
          fechaEfectiva,
        });
        salida.push(r.ok ? { linea, ok: true } : { linea, ok: false, motivo: r.motivo });
      } catch {
        salida.push({ linea, ok: false, motivo: 'No se ha podido registrar (error inesperado).' });
      }
    }
    setResultados(salida);
    setEnviando(false);
  }

  if (resultados !== null) {
    return (
      <Modal titulo="Verificar seleccionadas" cerrar={onTerminado} pie={<button type="button" className="principal" onClick={onTerminado}>Cerrar</button>}>
        <ResumenLoteMasivo resultados={resultados} />
      </Modal>
    );
  }

  if (lineas.length === 0) {
    return (
      <Modal titulo="Verificar seleccionadas" cerrar={onCerrar}>
        <Vacio titulo="Ninguna de las líneas seleccionadas admite verificar ahora" />
      </Modal>
    );
  }

  return (
    <Modal
      titulo="Verificar seleccionadas"
      cerrar={onCerrar}
      pie={
        <div className="fila" style={{ gap: 8 }}>
          <button type="button" className="exito" disabled={enviando} onClick={() => void verificarLote()}>
            {enviando ? 'Verificando…' : `Verificar ${String(lineas.length)} línea${lineas.length === 1 ? '' : 's'}`}
          </button>
          <button type="button" className="pequeno" disabled={enviando} onClick={onCerrar}>Cancelar</button>
        </div>
      }
    >
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lineas.map((l) => (
          <li key={l.id} className="pequena" style={{ borderTop: '1px solid var(--borde)', paddingTop: 8 }}>
            <strong>{l.ubicacionCodigo}</strong> <span className="suave">· {l.tipoCodigo}</span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

/**
 * FASE 7.2 — «Reportar incidencia» desde la vista offline de una OT. La
 * incidencia TODAVÍA no se crea sin conexión: con red se abre /reportar con
 * esta OT ya elegida; sin red se dice claramente, sin fingir que se guarda.
 */
function ReportarIncidenciaDesdeOffline({ idOt }: { idOt: string }): ReactNode {
  const conectividad = useConectividad();
  const navegar = useNavigate();
  const [aviso, setAviso] = useState(false);
  return (
    <div className="panel">
      <div className="panel-cuerpo">
        <button
          type="button"
          className="pequeno"
          style={{ minHeight: 44 }}
          onClick={() => {
            if (conectividad === 'ONLINE') navegar(`/reportar?ot=${idOt}`);
            else setAviso(true);
          }}
        >
          Reportar incidencia
        </button>
        {aviso && conectividad === 'OFFLINE' && (
          <p className="nota aviso" role="alert" style={{ marginTop: 8 }}>
            Necesitas conexión para reportar una incidencia.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * NC offline (FASE 5.4 §3/§4): sólo `NC_CREAR` (siempre disponible sobre
 * una OT no terminal) e `NC_INICIAR_TRATAMIENTO`/`NC_CERRAR` ENCADENADAS
 * sobre una NC de la que este dispositivo YA conoce el estado local — nunca
 * un listado de NC del servidor (fuera de alcance: el paquete offline no
 * trae NC, sólo OT/línea/documentos, ver `offline/db.ts::PaqueteOffline`).
 * `NC_ANULAR` queda fuera a propósito (mismo criterio que el backend).
 */
function PanelNoConformidadesOffline({
  idPerfil,
  roles,
  idOt,
  esEncargado,
  ttlVencido,
  otTerminal,
}: {
  idPerfil: string;
  roles: readonly { rol: string }[];
  idOt: string;
  esEncargado: boolean;
  ttlVencido: boolean;
  otTerminal: boolean;
}): ReactNode {
  const [mutacionesNc, setMutacionesNc] = useState<Mutacion[] | null>(null);
  const [mutacionesEvidenciaNc, setMutacionesEvidenciaNc] = useState<Mutacion[]>([]);
  const [creando, setCreando] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [gravedad, setGravedad] = useState<'LEVE' | 'GRAVE' | 'CRITICA'>('LEVE');
  const [guardandoCreacion, setGuardandoCreacion] = useState(false);
  const [errorCreacion, setErrorCreacion] = useState<string | null>(null);
  const [transicionAbierta, setTransicionAbierta] = useState<{ idOperacionClienteNc: string; tipo: TipoMutacionNc } | null>(null);
  const [motivo, setMotivo] = useState('');
  const [accionCorrectiva, setAccionCorrectiva] = useState('');
  const [guardandoTransicion, setGuardandoTransicion] = useState(false);
  const [errorTransicion, setErrorTransicion] = useState<string | null>(null);
  const [evidenciaAbiertaPara, setEvidenciaAbiertaPara] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    //  Mismo criterio que `cargarMutaciones` (FASE 5.6 §16): perfil bloqueado a mitad → se aborta en silencio.
    try {
      setMutacionesNc(await listarMutacionesNcDeOt(idPerfil, idOt));
      setMutacionesEvidenciaNc((await listarMutacionesEvidenciaDeOt(idPerfil, idOt)).filter((m) => (m.payload as { ambito: string }).ambito === 'NC'));
    } catch (e) {
      if (e instanceof ErrorPerfilBloqueado) return;
      throw e;
    }
  }, [idPerfil, idOt]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cadenas = agruparCadenasNc(mutacionesNc ?? []);
  const bloqueada = ttlVencido || otTerminal;

  /** Evidencias que penden de ESTA NC concreta — por `dependeDe` (todavía sin idNc real) o por el idNc real ya resuelto, igual dualismo que `agruparCadenasNc`. */
  function evidenciasDeCadena(cadena: (typeof cadenas)[number]): Mutacion[] {
    const ultima = cadena.mutaciones.at(-1);
    if (ultima === undefined) return [];
    return mutacionesEvidenciaNc.filter((m) => {
      if (m.dependeDe === ultima.idOperacionCliente) return true;
      return ultima.resultadoNc != null && m.dependeDe === null && m.idEntidad === ultima.resultadoNc.idNc;
    });
  }

  async function crear(): Promise<void> {
    if (descripcion.trim() === '') {
      setErrorCreacion('La descripción es obligatoria.');
      return;
    }
    setGuardandoCreacion(true);
    setErrorCreacion(null);
    const r = await crearMutacionNcCrear(idPerfil, roles, {
      idOt,
      descripcion: descripcion.trim(),
      gravedad,
      fechaEfectiva: new Date().toISOString(),
    });
    setGuardandoCreacion(false);
    if (!r.ok) {
      setErrorCreacion(r.motivo);
      return;
    }
    setCreando(false);
    setDescripcion('');
    setGravedad('LEVE');
    await cargar();
  }

  async function registrarTransicion(): Promise<void> {
    if (transicionAbierta === null) return;
    if (transicionAbierta.tipo === 'NC_CERRAR' && accionCorrectiva.trim() === '') {
      setErrorTransicion('La acción correctiva es obligatoria.');
      return;
    }
    setGuardandoTransicion(true);
    setErrorTransicion(null);
    const base = {
      idOt,
      idOperacionClienteNc: transicionAbierta.idOperacionClienteNc,
      fechaEfectiva: new Date().toISOString(),
    };
    const r =
      transicionAbierta.tipo === 'NC_INICIAR_TRATAMIENTO'
        ? await crearMutacionNcIniciarTratamiento(idPerfil, roles, { ...base, motivo: motivo.trim() === '' ? null : motivo.trim() })
        : await crearMutacionNcCerrar(idPerfil, roles, { ...base, accionCorrectiva: accionCorrectiva.trim() });
    setGuardandoTransicion(false);
    if (!r.ok) {
      setErrorTransicion(r.motivo);
      return;
    }
    setTransicionAbierta(null);
    setMotivo('');
    setAccionCorrectiva('');
    await cargar();
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>No conformidades</h2>
        {esEncargado && !bloqueada && !creando && (
          <button type="button" className="pequeno" onClick={() => setCreando(true)}>
            {ETIQUETA_TIPO_NC.NC_CREAR}
          </button>
        )}
      </div>
      <div className="panel-cuerpo">
        {cadenas.length === 0 && !creando && <Vacio titulo="Sin no conformidades registradas sin conexión" />}

        {cadenas.length > 0 && (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {cadenas.map((cadena) => {
              const creacionPayload = cadena.creacion.payload as PayloadNcCrear;
              const ultima = cadena.mutaciones.at(-1)!;
              const estadoActualNc = estadoLocalNc(cadena);
              const bloqueadaEstaCadena = ultima.estadoSync === 'RECHAZADA' || ultima.estadoSync === 'EN_ESPERA';
              const puedeIniciar = esEncargado && !bloqueada && !bloqueadaEstaCadena && estadoActualNc === 'ABIERTA';
              //  FASE 7.2: el cierre formal exige análisis de causa, acciones y
              //  resolución (0054) — nada de eso existe offline. No se encola
              //  una operación que el servidor va a rechazar: se explica.
              const cierreSoloOnline = esEncargado && !bloqueada && !bloqueadaEstaCadena && estadoActualNc === 'EN_TRATAMIENTO';
              return (
                <li key={cadena.creacion.idOperacionCliente} className="datos" style={{ border: '1px solid var(--linea, #ddd)', borderRadius: 8, padding: 10 }}>
                  <div className="fila" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>
                      {ultima.resultadoNc?.codigo ?? 'NC sin sincronizar'} · {ETIQUETA_GRAVEDAD_NC[creacionPayload.gravedad]}
                    </strong>
                    <span className="suave pequena">{estadoActualNc ?? '—'}</span>
                  </div>
                  <p className="pequena" style={{ margin: '4px 0' }}>{creacionPayload.descripcion}</p>
                  <p className="suave pequena" style={{ margin: 0 }}>{textoEstado(ultima)}</p>
                  {ultima.ultimoError !== null && (ultima.estadoSync === 'RECHAZADA' || ultima.estadoSync === 'EN_ESPERA') && (
                    <p className="suave pequena" style={{ margin: 0 }}>{ultima.ultimoError.mensaje}</p>
                  )}
                  {cierreSoloOnline && (
                    <p className="suave pequena" style={{ margin: '6px 0 0' }} role="note">
                      El cierre de una NC requiere conexión.
                    </p>
                  )}
                  {(puedeIniciar || (esEncargado && !bloqueada && !bloqueadaEstaCadena)) && (
                    <div className="fila" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {puedeIniciar && (
                        <button
                          type="button"
                          className="pequeno"
                          onClick={() => setTransicionAbierta({ idOperacionClienteNc: ultima.idOperacionCliente, tipo: 'NC_INICIAR_TRATAMIENTO' })}
                        >
                          {ETIQUETA_TIPO_NC.NC_INICIAR_TRATAMIENTO}
                        </button>
                      )}
                      {evidenciaAbiertaPara !== ultima.idOperacionCliente && (
                        <button
                          type="button"
                          className="pequeno"
                          onClick={() => setEvidenciaAbiertaPara(ultima.idOperacionCliente)}
                        >
                          Adjuntar evidencia
                        </button>
                      )}
                    </div>
                  )}

                  {transicionAbierta !== null && transicionAbierta.idOperacionClienteNc === ultima.idOperacionCliente && (
                    // FASE 5.7-C C05 — ver comentario en `FormularioEvidenciaOffline`: mismo `min(…, 90vw)`, mismo motivo.
                    <div className="datos" style={{ marginTop: 8, maxWidth: 'min(380px, 90vw)' }}>
                      {transicionAbierta.tipo === 'NC_INICIAR_TRATAMIENTO' && (
                        <div className="campo">
                          <label htmlFor={`nc-motivo-${ultima.idOperacionCliente}`}>Motivo (opcional)</label>
                          <textarea id={`nc-motivo-${ultima.idOperacionCliente}`} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                        </div>
                      )}
                      {transicionAbierta.tipo === 'NC_CERRAR' && (
                        <div className="campo">
                          <label htmlFor={`nc-accion-${ultima.idOperacionCliente}`}>Acción correctiva</label>
                          <textarea id={`nc-accion-${ultima.idOperacionCliente}`} value={accionCorrectiva} onChange={(e) => setAccionCorrectiva(e.target.value)} />
                        </div>
                      )}
                      {errorTransicion !== null && <p className="nota error" role="alert">{errorTransicion}</p>}
                      <div className="fila" style={{ gap: 8 }}>
                        <button type="button" className="principal" disabled={guardandoTransicion} onClick={() => void registrarTransicion()}>
                          {guardandoTransicion ? 'Guardando…' : `Registrar ${ETIQUETA_TIPO_NC[transicionAbierta.tipo].toLowerCase()}`}
                        </button>
                        <button type="button" className="pequeno" onClick={() => { setTransicionAbierta(null); setErrorTransicion(null); }}>Cancelar</button>
                      </div>
                    </div>
                  )}

                  {evidenciasDeCadena(cadena).length > 0 && (
                    <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {evidenciasDeCadena(cadena).map((m) => (
                        <li key={m.idOperacionCliente} className="pequena">
                          Evidencia «{(m.payload as { nombreArchivo: string }).nombreArchivo}» · {textoEstado(m).toLowerCase()}
                        </li>
                      ))}
                    </ul>
                  )}

                  {evidenciaAbiertaPara === ultima.idOperacionCliente && (
                    <div style={{ marginTop: 8 }}>
                      <FormularioEvidenciaOffline
                        idPerfil={idPerfil}
                        roles={roles}
                        idOt={idOt}
                        destino={{ ambito: 'NC', idOperacionClienteNc: ultima.idOperacionCliente }}
                        onGuardado={() => { setEvidenciaAbiertaPara(null); void cargar(); }}
                        onCancelar={() => setEvidenciaAbiertaPara(null)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {creando && (
          // FASE 5.7-C C05 — ver comentario en `FormularioEvidenciaOffline`: mismo `min(420px, 90vw)`, mismo motivo.
          <div className="datos" style={{ marginTop: 12, maxWidth: 'min(420px, 90vw)' }}>
            <div className="campo">
              <label htmlFor="nc-nueva-descripcion">Descripción</label>
              <textarea id="nc-nueva-descripcion" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="nc-nueva-gravedad">Gravedad</label>
              <select
                id="nc-nueva-gravedad"
                value={gravedad}
                onChange={(e) => setGravedad(e.target.value as 'LEVE' | 'GRAVE' | 'CRITICA')}
              >
                <option value="LEVE">Leve</option>
                <option value="GRAVE">Grave</option>
                <option value="CRITICA">Crítica</option>
              </select>
            </div>
            {errorCreacion !== null && <p className="nota error" role="alert">{errorCreacion}</p>}
            <div className="fila" style={{ gap: 8 }}>
              <button type="button" className="principal" disabled={guardandoCreacion} onClick={() => void crear()}>
                {guardandoCreacion ? 'Guardando…' : 'Registrar NC'}
              </button>
              <button type="button" className="pequeno" onClick={() => { setCreando(false); setErrorCreacion(null); }}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** F14.2 — el OPERARIO ve y ejecuta su OT en «Mis trabajos» (misma pantalla con o sin conexión). */
export function PaginaOfflineOt(): ReactNode {
  const { roles } = useSesionOfflineActiva();
  const { idOt } = useParams<{ idOt: string }>();
  if (!roles.some((r) => r.rol === 'ENCARGADO') && roles.some((r) => r.rol === 'OPERARIO')) {
    return <Navigate to={`/mis-ots/${idOt ?? ''}`} replace />;
  }
  return <PaginaOfflineOtGestion />;
}

function PaginaOfflineOtGestion(): ReactNode {
  //  A diferencia de `usuario` (`useSesion()`), esta sesión SIEMPRE existe
  //  mientras este componente está montado: `PuertaOfflineSegura`
  //  (`offline-perfil.tsx`) desmonta el árbol entero en cuanto el perfil
  //  se bloquea (§42) — nunca queda una vista a medio poblar con una
  //  sesión que ya no existe, así que no hace falta la guarda defensiva
  //  que sí necesitaba `usuario === null`.
  const { perfilId, roles } = useSesionOfflineActiva();
  const { idOt } = useParams<{ idOt: string }>();
  const [detalle, setDetalle] = useState<Awaited<ReturnType<typeof leerOtLocal>> | null | undefined>(undefined);
  const [meta, setMeta] = useState<MetaOffline | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [evidenciaOtAbierta, setEvidenciaOtAbierta] = useState(false);
  const [mutacionesEvidenciaOt, setMutacionesEvidenciaOt] = useState<Mutacion[]>([]);
  const [errorLectura, setErrorLectura] = useState<string | null>(null);
  //  FASE 5.7-C C06/C07/C08 — mutaciones de línea ACTIVAS de esta OT,
  //  leídas UNA vez aquí (reutilizando `listarMutacionesNoSincronizadas`
  //  de C03) para saber, por línea, qué acción masiva tiene sentido
  //  ofrecer. Cada `LineaOfflineFila` sigue leyendo sus propias mutaciones
  //  para su detalle de fila — esto NUNCA sustituye esa lectura, sólo
  //  añade la vista "de conjunto" que la fila individual no necesita.
  const [mutacionesLineasOt, setMutacionesLineasOt] = useState<readonly Mutacion[]>([]);
  //  F14.2 — todas las operaciones locales de esta OT (cualquier estado): «trabajo iniciado» para la caducidad.
  const [operacionesOt, setOperacionesOt] = useState<readonly Mutacion[]>([]);
  const [seleccionLineas, setSeleccionLineas] = useState<ReadonlySet<string>>(new Set());
  const [confirmandoSeleccion, setConfirmandoSeleccion] = useState(false);
  const [verificandoSeleccion, setVerificandoSeleccion] = useState(false);

  useEffect(() => {
    if (idOt === undefined) return;
    let vivo = true;
    void (async () => {
      try {
        const [detalleCargado, metaCargada, evidenciasCargadas, noSincronizadas, deLaOt] = await Promise.all([
          leerOtLocal(perfilId, idOt),
          leerMeta(perfilId),
          listarMutacionesEvidenciaDeOt(perfilId, idOt),
          listarMutacionesNoSincronizadas(perfilId),
          listarMutacionesDeOt(perfilId, idOt),
        ]);
        if (!vivo) return;
        setErrorLectura(null);
        setDetalle(detalleCargado);
        setMeta(metaCargada);
        setOperacionesOt(deLaOt);
        setMutacionesEvidenciaOt(evidenciasCargadas);
        setMutacionesLineasOt(noSincronizadas.filter((m) => m.idOt === idOt && esMutacionDeLinea(m)));
        //  Cualquier recarga (acción individual, sincronización, o el
        //  cierre de un panel de acción masiva) invalida la selección
        //  anterior: las líneas que la componían pueden haber cambiado de
        //  estado y ya no ser elegibles para la misma acción.
        setSeleccionLineas(new Set());
      } catch {
        //  FASE 5.7-A §9/§33 — sin este `catch`, un fallo de lectura
        //  (corrupción, `ErrorDescifrado`) dejaba `detalle` en `undefined`
        //  para siempre: la pantalla se quedaba en `<Cargando/>` eterno,
        //  sin ningún mensaje.
        if (vivo) setErrorLectura(MENSAJE_FALLO_LECTURA_LOCAL);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [perfilId, idOt, recarga]);

  if (errorLectura !== null) {
    return (
      <Shell titulo="Orden de trabajo" migas={[{ texto: 'Trabajar sin conexión', a: '/offline' }, { texto: '—' }]}>
        <Vacio titulo={errorLectura} />
      </Shell>
    );
  }
  if (detalle === undefined) return <Shell titulo="Orden de trabajo"><Cargando /></Shell>;
  if (detalle === null) {
    return (
      <Shell titulo="Orden de trabajo" migas={[{ texto: 'Trabajar sin conexión', a: '/offline' }, { texto: '—' }]}>
        <Vacio titulo="No está disponible sin conexión">
          Esta orden de trabajo no forma parte de los datos descargados para este dispositivo.
        </Vacio>
      </Shell>
    );
  }

  const { ot, lineas, documentos } = detalle;
  const esEncargado = roles.some((r) => r.rol === 'ENCARGADO');
  //  F14.2 — `ttlVencido` conserva su nombre (lo usan las filas y paneles), pero ya no es «más de 24 h»: es «más de
  //  72 h y esta unidad no está iniciada en este dispositivo» (`offline/caducidad.ts`). La unidad es la LÍNEA
  //  (`ttlVencidoDe`); lo de ámbito OT (foto de la OT, NC) pide alguna línea iniciada y no inicia ninguna — igual que
  //  `mutaciones.ts::comprobarCaducidad`, que es la barrera real. Entre 24 y 72 h, sólo aviso.
  const nivel = meta === null ? 'VIGENTE' : nivelCaducidad(meta);
  const lineaIniciadaAqui = (l: LineaOfflineLocal): boolean => lineaIniciadaEnDispositivo(l, operacionesOt.filter((m) => m.idEntidad === l.id));
  const ttlVencidoDe = (l: LineaOfflineLocal): boolean => !decidirPorCaducidad(nivel, lineaIniciadaAqui(l)).permitido;
  const ttlVencido = !decidirPorCaducidad(nivel, lineas.some(lineaIniciadaAqui)).permitido;
  const otTerminal = ot.estado === 'ANULADA' || ot.estado === 'NO_REALIZADA';
  const evidenciasOtNoSincronizadas = mutacionesEvidenciaOt.filter(
    (m) => (m.payload as { ambito: string }).ambito === 'OT' && m.estadoSync !== 'SINCRONIZADA',
  );

  //  FASE 5.7-C C06/C07/C08 — mismo criterio EXACTO que decide qué botones
  //  de acción individual pinta `LineaOfflineFila` (esEncargado + TTL +
  //  ventana + estado PROYECTADO, nunca el estado crudo del paquete): una
  //  línea nunca aparece seleccionable para una acción masiva que su fila
  //  individual tampoco ofrecería.
  function accionesMasivasDe(l: LineaOfflineLocal): readonly TipoMutacionLinea[] {
    if (!esEncargado || ttlVencidoDe(l) || otTerminal || (l.fueraDeVentana ?? false)) return [];
    const activas = mutacionesLineasOt.filter((m) => m.idEntidad === l.id && esMutacionActiva(m));
    return accionesLocalesDisponibles(proyectarEstadoLinea(l.estado, activas));
  }
  const lineasSeleccionables = lineas.filter((l) => {
    const disp = accionesMasivasDe(l);
    return disp.includes('LINEA_CONFIRMAR') || disp.includes('LINEA_VERIFICAR');
  });
  const lineasSeleccionadasConfirmar = lineas.filter(
    (l) => seleccionLineas.has(l.id) && accionesMasivasDe(l).includes('LINEA_CONFIRMAR'),
  );
  const lineasSeleccionadasVerificar = lineas.filter(
    (l) => seleccionLineas.has(l.id) && accionesMasivasDe(l).includes('LINEA_VERIFICAR'),
  );

  function alternarSeleccionLinea(idLinea: string): void {
    setSeleccionLineas((prev) => {
      const s = new Set(prev);
      if (s.has(idLinea)) s.delete(idLinea);
      else s.add(idLinea);
      return s;
    });
  }

  return (
    <Shell
      titulo={ot.codigo}
      migas={[{ texto: 'Trabajar sin conexión', a: '/offline' }, { texto: ot.codigo }]}
      subtitulo={<EstadoOt estado={ot.estado} />}
    >
      <div className="panel">
        <div className="panel-cabecera">
          <h2>Datos</h2>
          {esEncargado && !ttlVencido && !otTerminal && !evidenciaOtAbierta && (
            <button type="button" className="pequeno" onClick={() => setEvidenciaOtAbierta(true)}>
              Adjuntar evidencia
            </button>
          )}
        </div>
        <div className="panel-cuerpo">
          <div className="datos">
            <div><div className="etiqueta-campo">Centro</div><div>{ot.centroDescripcion} <span className="cod suave">{ot.centroCodigo}</span></div></div>
            <div><div className="etiqueta-campo">Sala</div><div>{ot.salaDescripcion} <span className="cod suave">{ot.salaCodigo}</span></div></div>
            <div><div className="etiqueta-campo">Inicio programado</div><div>{fechaHora(ot.inicioProgramadoEn)}</div></div>
          </div>
          {ot.observaciones !== null && ot.observaciones !== '' && <p style={{ marginTop: 12 }}>{ot.observaciones}</p>}
          {ot.fueraDeVentana === true && (
            <p className="nota" style={{ marginTop: 12 }}>
              Esta OT ya no está en tu ventana de trabajo — se conserva sólo porque tiene cambios pendientes de sincronizar.
            </p>
          )}
          {evidenciaOtAbierta && (
            <div style={{ marginTop: 12 }}>
              <FormularioEvidenciaOffline
                idPerfil={perfilId}
                roles={roles}
                idOt={ot.id}
                destino={{ ambito: 'OT' }}
                onGuardado={() => { setEvidenciaOtAbierta(false); setRecarga((n) => n + 1); }}
                onCancelar={() => setEvidenciaOtAbierta(false)}
              />
            </div>
          )}
          {evidenciasOtNoSincronizadas.length > 0 && (
            <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {evidenciasOtNoSincronizadas.map((m) => (
                <li key={m.idOperacionCliente} className="pequena">
                  Evidencia «{(m.payload as { nombreArchivo: string }).nombreArchivo}» · {textoEstado(m).toLowerCase()}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {esEncargado && nivel === 'CADUCADO' && (
        <p className="nota error" role="alert">{MOTIVO_TRABAJO_NUEVO_BLOQUEADO}</p>
      )}
      {esEncargado && nivel === 'AVISO' && (
        <p className="nota aviso" role="status">{AVISO_DATOS_DESACTUALIZADOS}</p>
      )}

      {confirmandoSeleccion && (
        <PanelConfirmarSeleccionadas
          idPerfil={perfilId}
          roles={roles}
          idOt={ot.id}
          lineas={lineasSeleccionadasConfirmar}
          onCerrar={() => setConfirmandoSeleccion(false)}
          onTerminado={() => {
            setConfirmandoSeleccion(false);
            setRecarga((n) => n + 1);
          }}
        />
      )}

      {verificandoSeleccion && (
        <PanelVerificarSeleccionadas
          idPerfil={perfilId}
          roles={roles}
          idOt={ot.id}
          lineas={lineasSeleccionadasVerificar}
          onCerrar={() => setVerificandoSeleccion(false)}
          onTerminado={() => {
            setVerificandoSeleccion(false);
            setRecarga((n) => n + 1);
          }}
        />
      )}

      <div className="panel">
        <div className="panel-cabecera"><h2>Líneas</h2></div>
        {esEncargado && lineasSeleccionables.length > 0 && !confirmandoSeleccion && !verificandoSeleccion && (
          <div className="barra-bulk">
            <div className="fila barra-bulk-fila">
              <button
                type="button"
                className="pequeno"
                onClick={() => setSeleccionLineas(new Set(lineasSeleccionables.map((l) => l.id)))}
              >
                Seleccionar todas
              </button>
              <button
                type="button"
                className="pequeno"
                onClick={() => setSeleccionLineas(new Set())}
                disabled={seleccionLineas.size === 0}
              >
                Limpiar selección
              </button>
              <span className="pequena suave barra-bulk-contador">
                {seleccionLineas.size} seleccionada{seleccionLineas.size === 1 ? '' : 's'}
              </span>
              <span className="barra-bulk-relleno" />
              {lineasSeleccionadasVerificar.length > 0 && (
                <button type="button" className="exito" onClick={() => setVerificandoSeleccion(true)}>
                  Verificar seleccionadas ({lineasSeleccionadasVerificar.length})
                </button>
              )}
              {lineasSeleccionadasConfirmar.length > 0 && (
                <button type="button" className="principal" onClick={() => setConfirmandoSeleccion(true)}>
                  Confirmar seleccionadas ({lineasSeleccionadasConfirmar.length})
                </button>
              )}
            </div>
          </div>
        )}
        <div className="panel-cuerpo sin-relleno">
          {lineas.length === 0 ? (
            <Vacio titulo="Sin líneas" />
          ) : (
            <div className="tabla-desliza tabla-lineas-offline">
              <table>
                <thead>
                  <tr>
                    {esEncargado && lineasSeleccionables.length > 0 && <th className="col-check" aria-label="Seleccionar" />}
                    <th>Ubicación</th><th>Tipo</th><th>Descripción</th><th>Asignados</th><th>Estado</th>
                    {esEncargado && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((l) => (
                    <LineaOfflineFila
                      key={l.id}
                      idPerfil={perfilId}
                      roles={roles}
                      idOt={ot.id}
                      linea={l}
                      esEncargado={esEncargado}
                      ttlVencido={ttlVencidoDe(l)}
                      mostrarCasillaSeleccion={esEncargado && lineasSeleccionables.length > 0}
                      seleccionable={accionesMasivasDe(l).includes('LINEA_CONFIRMAR') || accionesMasivasDe(l).includes('LINEA_VERIFICAR')}
                      seleccionada={seleccionLineas.has(l.id)}
                      onAlternarSeleccion={() => alternarSeleccionLinea(l.id)}
                      onCambio={() => setRecarga((n) => n + 1)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* FASE 9.6-B1: material previsto, sólo lectura. F14.2 (INV-DEBT-PREVISTOS-OFFLINE): viene en el paquete y se
              ve sin conexión. Un paquete anterior a F14.2 (sin el campo) lo sigue leyendo de la OT con conexión
              (`/api/ots/:id/previstos`, F14.1). */}
          {lineas.some((l) => l.materialesPrevistos !== undefined)
            ? <MaterialesPrevistosDelPaquete lineas={lineas} />
            : <MaterialesPrevistosDeOt idOt={ot.id} lineas={lineas} />}
        </div>
      </div>

      <ReportarIncidenciaDesdeOffline idOt={ot.id} />

      {esEncargado && (
        <PanelNoConformidadesOffline
          idPerfil={perfilId}
          roles={roles}
          idOt={ot.id}
          esEncargado={esEncargado}
          ttlVencido={ttlVencido}
          otTerminal={otTerminal}
        />
      )}

      <div className="panel">
        <div className="panel-cabecera"><h2>Documentación aplicable</h2></div>
        <div className="panel-cuerpo sin-relleno">
          {documentos.length === 0 ? (
            <Vacio titulo="Sin documentos" />
          ) : (
            <ul style={{ margin: 0, padding: 16, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {documentos.map((d) => (
                <li key={d.id} className="fila" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    {d.titulo} <span className="cod suave">{d.codigo} · v{d.version}</span>
                  </span>
                  <BotonAbrirDocumentoLocal idPerfil={perfilId} doc={d} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ------------------------------------------------- FASE 9.6-B1: material previsto

/**
 * F14.2 — «Materiales previstos» desde el paquete local: sólo lectura, con o sin conexión. PREVISTO no es entregado
 * ni consumido; nada de esto se sincroniza (el consumo sigue siendo sólo con conexión).
 */
function MaterialesPrevistosDelPaquete({ lineas }: { lineas: readonly LineaOfflineLocal[] }): ReactNode {
  const conMaterial: { id: string; orden: number; ubicacionCodigo: string; materialesPrevistos: readonly PrevistoOperario[] }[] = lineas
    .filter((l) => (l.materialesPrevistos ?? []).length > 0)
    .map((l) => ({ id: l.id, orden: l.orden, ubicacionCodigo: l.ubicacionCodigo, materialesPrevistos: l.materialesPrevistos ?? [] }));
  if (conMaterial.length === 0) return null;
  return (
    <section className="panel previstos-operario" aria-label="Materiales previstos">
      <div className="panel-cabecera"><h2>Materiales previstos</h2></div>
      <div className="panel-cuerpo">
        {(
          conMaterial.map((l) => (
            <div key={l.id} className="previstos-linea">
              <h3 className="pequena"><span className="mono">#{l.orden}</span> {l.ubicacionCodigo}</h3>
              <ul className="lista-previstos">
                {(l.materialesPrevistos ?? []).map((m) => (
                  <li key={m.id} className="fila-previsto"><div><strong>{m.articulo.descripcion}</strong> · {m.cantidad} {m.unidad}
                    {m.instrucciones !== null && <div className="suave pequena">{m.instrucciones}</div>}</div></li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/**
 * F14.1 (defecto F14.0 #1) — «Materiales previstos» de la OT para el ENCARGADO: `/api/ots/:id/previstos`
 * (`previstos_ot:leer`, su ámbito de centros). Sólo los vigentes (no retirados), por línea. F14.2: sólo para un
 * paquete guardado antes de F14.2 (sin previstos); sin conexión, se avisa de que hace falta actualizar.
 */
function MaterialesPrevistosDeOt({ idOt, lineas }: { idOt: string; lineas: readonly LineaOfflineLocal[] }): ReactNode {
  const conectividad = useConectividad();
  const [previstos, setPrevistos] = useState<PrevistosOt['previstos'] | null>(null);
  useEffect(() => {
    if (conectividad === 'OFFLINE') return;
    let vivo = true;
    api.get<PrevistosOt>(`/api/ots/${idOt}/previstos`)
      .then((r) => { if (vivo) setPrevistos(r.previstos.filter((p) => p.retirada === null)); })
      .catch(() => { if (vivo) setPrevistos([]); });
    return () => { vivo = false; };
  }, [idOt, conectividad]);
  if (conectividad !== 'OFFLINE' && (previstos === null || previstos.length === 0)) return null;
  const porLinea = new Map<string, PrevistosOt['previstos']>();
  for (const p of previstos ?? []) porLinea.set(p.idLinea, [...(porLinea.get(p.idLinea) ?? []), p]);
  const lineasConMaterial = lineas.filter((l) => porLinea.has(l.id));
  return (
    <section className="panel previstos-operario" aria-label="Materiales previstos">
      <div className="panel-cabecera"><h2>Materiales previstos</h2></div>
      <div className="panel-cuerpo">
        {conectividad === 'OFFLINE' ? <p className="suave pequena">Actualiza los datos con conexión para ver aquí el material previsto.</p> : (
          lineasConMaterial.map((l) => (
            <div key={l.id} className="previstos-linea">
              <h3 className="pequena"><span className="mono">#{l.orden}</span> {l.ubicacionCodigo}</h3>
              <ul className="lista-previstos">
                {(porLinea.get(l.id) ?? []).map((m) => (
                  <li key={m.id} className="fila-previsto"><div><strong>{m.articulo.descripcion}</strong> · {m.cantidad} {m.unidad.codigo}
                    {m.instrucciones !== null && <div className="suave pequena">{m.instrucciones}</div>}</div></li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
